import type { Signal } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { EMPTY, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import type { RtButtonVariant } from '@core/user-interface-legacy';
import { BulkSelectComponent, CheckboxComponent } from '@core/user-interface-legacy';

import { BulkActionsController } from '../bulk-actions.controller';
import { BulkActionsSelectionTracker } from '../bulk-actions.selection-tracker';
import { BULK_ACTIONS_BATCH_SELECT_STRATEGY } from './bulk-actions-batch-select.strategy';
import type {
  BulkActionsBatchSelectState,
  BulkActionsSelectState,
} from './bulk-actions-batch-select.strategy';

const INITIAL_STRATEGY_STATE: BulkActionsBatchSelectState = {
  selectState: 'unchecked',
  selectedCount: 0,
};

@Component({
  selector: 'rt-bulk-actions-batch-select',
  templateUrl: './bulk-actions-batch-select.component.html',
  styleUrls: ['./bulk-actions-batch-select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.select]': 'type() === "select"',
    '[class.checkbox]': 'type() === "checkbox"',
    '[class.is-disabled]': 'disabled()',
  },
  imports: [BulkSelectComponent, CheckboxComponent],
})
export class BulkActionsBatchSelectComponent {
  private readonly bulkCtrl = inject(BulkActionsController);
  private readonly tracker = inject(BulkActionsSelectionTracker);
  private readonly strategy = inject(BULK_ACTIONS_BATCH_SELECT_STRATEGY, { optional: true });

  private readonly items = toSignal(this.tracker.items$, { initialValue: [] });

  private readonly currentViewSelectedCount = toSignal(
    this.tracker.state$.pipe(map(state => state.currentViewSelectedCount)),
    { initialValue: 0 },
  );

  private readonly currentViewTotalCount = toSignal(
    this.tracker.state$.pipe(map(state => state.currentViewTotalCount)),
    { initialValue: 0 },
  );

  private readonly strategyState = toSignal(this.strategy?.state$ ?? EMPTY, {
    initialValue: INITIAL_STRATEGY_STATE,
  });

  private readonly isPerStage = computed(
    () => this.stageId() != null && !!this.strategy?.stageState$,
  );

  private readonly stageState: Signal<BulkActionsSelectState>;

  private readonly selected = computed(() => {
    const total = this.items().length;
    return this.currentViewSelectedCount() === total && total > 0;
  });

  private readonly indeterminate = computed(
    () =>
      this.currentViewSelectedCount() > 0 &&
      this.currentViewSelectedCount() < this.currentViewTotalCount(),
  );

  protected readonly active = toSignal(this.bulkCtrl.active$, {
    initialValue: this.bulkCtrl.active,
  });

  protected readonly disabled = computed(() =>
    this.isPerStage() ? false : this.items().length === 0,
  );

  protected readonly displaySelected = computed(() => {
    if (this.isPerStage()) return this.stageState() === 'checked';
    return this.strategy ? this.strategyState().selectState === 'checked' : this.selected();
  });

  protected readonly displayIndeterminate = computed(() => {
    if (this.isPerStage()) return this.stageState() === 'indeterminate';
    return this.strategy
      ? this.strategyState().selectState === 'indeterminate'
      : this.indeterminate();
  });

  protected readonly displayCount = computed(() =>
    this.strategy ? this.strategyState().selectedCount : this.currentViewSelectedCount(),
  );

  public readonly stageId = input<number | undefined>(undefined);
  public readonly type = input<'select' | 'checkbox'>('select');
  public readonly variant = input<Extract<RtButtonVariant, 'tertiary' | 'outline'>>('tertiary');

  constructor() {
    this.stageState = toSignal(
      toObservable(this.stageId).pipe(
        switchMap(id =>
          id != null && this.strategy?.stageState$
            ? this.strategy.stageState$(id)
            : of<BulkActionsSelectState>('unchecked'),
        ),
      ),
      { initialValue: <BulkActionsSelectState>'unchecked' },
    );
  }

  public handleSelect(toggle: boolean): void {
    const stageId = this.stageId();
    if (this.isPerStage() && stageId != null && this.strategy?.toggleStage) {
      this.strategy.toggleStage(stageId, toggle);
      return;
    }
    if (this.strategy) {
      this.strategy.toggle(toggle);
      return;
    }
    this.bulkCtrl.selectItems(this.items(), toggle);
  }
}
